import type { CreateFormData } from "@/components/create/types";
import {
  evaluateFrontDesignReadiness,
  type FrontDesignField,
} from "@/lib/agent/designBriefReadiness";
import type { AgentPhase, PresetActionId } from "@/types/createAgent";
import type { DesignSpec } from "@/types/designSpec";

export type AgentActionReadinessReason =
  | "allowed"
  | "front_design_too_thin"
  | "front_image_required"
  | "front_approval_required"
  | "back_prompt_required"
  | "back_image_required"
  | "back_image_required_for_quote";

export type AgentActionReadinessMissingField =
  | FrontDesignField
  | "frontImage"
  | "frontApproval"
  | "backPrompt"
  | "backImage";

export interface EvaluateAgentActionReadinessParams {
  actionId: PresetActionId | string;
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  hasFrontImage: boolean;
  hasBackImage: boolean;
  frontApproved: boolean;
  /** A confirmed style or closure in the canonical brief can describe a Back without prose. */
  hasBackDesignDirection?: boolean;
}

export interface AgentActionReadiness {
  allowed: boolean;
  reason: AgentActionReadinessReason;
  missingFields: AgentActionReadinessMissingField[];
  /**
   * v2-engine copy / v3 compose input — never shipped verbatim on v3. On the
   * v2/legacy path this is the user-facing block message; on v3 a blocked click
   * runs the deterministic pipeline where composeReply always rewrites the reply
   * from this reason + missingFields, so the raw string never reaches the user.
   */
  message: string;
  suggestedActionIds?: PresetActionId[];
}

const ALLOWED_RESULT: AgentActionReadiness = {
  allowed: true,
  reason: "allowed",
  missingFields: [],
  message: "",
};

function blockedResult(
  reason: AgentActionReadinessReason,
  missingFields: AgentActionReadinessMissingField[],
  message: string,
  suggestedActionIds?: PresetActionId[]
): AgentActionReadiness {
  return {
    allowed: false,
    reason,
    missingFields,
    message,
    ...(suggestedActionIds ? { suggestedActionIds } : {}),
  };
}

function hasText(value: string | undefined): boolean {
  return (value?.trim().length ?? 0) > 0;
}

/** A Back style or closure deliberately chosen in Details is enough to render. */
export function hasConfirmedBackDesignDirection(
  draft: DesignSpec | null | undefined
): boolean {
  return Boolean(
    draft &&
    (["backPrompt", "backStyle", "closure"] as const).some(field => {
      const provenance = draft.provenance[`visual.${field}`];
      return Boolean(
        hasText(draft.visual[field]) &&
        provenance?.confirmed &&
        provenance.source !== "inferred"
      );
    })
  );
}

/**
 * A restored session can legitimately already be in a back phase before the
 * persisted `frontApproved` flag has round-tripped. Reaching a back phase is a
 * durable approval signal; merely being in `front_review` is not, because the
 * customer's next turn may reject the render or request a change.
 */
export function isFrontApprovedForPhase(params: {
  phase: AgentPhase;
  hasFrontImage: boolean;
  frontApproved: boolean;
}): boolean {
  return (
    params.frontApproved ||
    (params.hasFrontImage &&
      (params.phase === "back_design" || params.phase === "back_review"))
  );
}

/**
 * An explicit back request against a front render the customer has already been
 * shown, and has not objected to, IS the approval — the same implicit-approval
 * rule buildCandidate applies to restored back phases. Requiring a separate
 * approval click first blocked the customer who simply described the back from
 * `front_review`, which is where most back-phase clicks originate.
 */
export function hasExplicitBackDesignRequest(
  formState: Partial<CreateFormData>,
  hasBackDesignDirection = false
): boolean {
  return (
    hasText(formState.backPrompt) ||
    hasBackDesignDirection ||
    formState.generateBack === true ||
    hasBackUploadReference(formState)
  );
}

export function hasBackUploadReference(
  formState: Partial<CreateFormData>
): boolean {
  const uploadedBackImageUrls = (formState as Record<string, unknown>)
    .uploadedBackImageUrls;
  const uploadedBackImageCount = (formState as Record<string, unknown>)
    .uploadedBackImageCount;

  return !!(
    (Array.isArray(formState.uploadedBackImages) &&
      formState.uploadedBackImages.length > 0) ||
    (Array.isArray(uploadedBackImageUrls) &&
      uploadedBackImageUrls.length > 0) ||
    (typeof uploadedBackImageCount === "number" && uploadedBackImageCount > 0)
  );
}

export function evaluateAgentActionReadiness({
  actionId,
  phase,
  formState,
  hasFrontImage,
  hasBackImage,
  frontApproved,
  hasBackDesignDirection = false,
}: EvaluateAgentActionReadinessParams): AgentActionReadiness {
  const effectiveFrontApproved = isFrontApprovedForPhase({
    phase,
    hasFrontImage,
    frontApproved,
  });

  switch (actionId) {
    case "generate_dress_front": {
      // A front render already exists: this click is a re-render of a design
      // that has already proven generatable. evaluateFrontDesignReadiness
      // reports readyToGenerate:false for that state (it guards FIRST renders),
      // which used to fall through to a nonsense "pick more details" block on
      // an intentional re-roll.
      if (hasFrontImage) {
        return ALLOWED_RESULT;
      }

      const readiness = evaluateFrontDesignReadiness(formState, {
        hasFrontImage,
      });

      if (readiness.readyToGenerate) {
        return ALLOWED_RESULT;
      }

      return blockedResult(
        "front_design_too_thin",
        readiness.missingFields,
        "Choose one more detail before creating the front preview, such as the color, shape, or fabric.",
        [
          "set_color",
          "set_silhouette",
          "set_neckline",
          "set_sleeve",
          "set_length",
          "set_fabric",
        ]
      );
    }

    case "generate_dress_back": {
      if (!hasFrontImage) {
        return blockedResult(
          "front_image_required",
          ["frontImage"],
          "Generate the front view before creating the back.",
          ["generate_dress_front"]
        );
      }

      if (
        !effectiveFrontApproved &&
        !hasExplicitBackDesignRequest(formState, hasBackDesignDirection)
      ) {
        return blockedResult(
          "front_approval_required",
          ["frontApproval"],
          "Approve the front design before creating the back.",
          ["continue_to_back_design"]
        );
      }

      if (
        !hasText(formState.backPrompt) &&
        !hasBackDesignDirection &&
        !hasBackUploadReference(formState)
      ) {
        return blockedResult(
          "back_prompt_required",
          ["backPrompt"],
          "Tell me what the back should look like or upload a back reference before I generate it.",
          ["set_back_prompt", "set_back_style"]
        );
      }

      return ALLOWED_RESULT;
    }

    case "submit_quote": {
      if (!hasFrontImage) {
        return blockedResult(
          "front_image_required",
          ["frontImage"],
          "Generate the front view before requesting a quote.",
          ["generate_dress_front"]
        );
      }
      // A back the customer specified but never rendered is not part of a
      // design anyone has seen, and the ticket is what production builds from:
      // a $1,700 ticket went to manufacturing carrying a back spec with no
      // render behind it.
      //
      // A confirmed Back direction in the canonical draft is part of the
      // ticket regardless of the current tab. A flat backPrompt or reference
      // can be only a mention during front review, so those still require a
      // Back phase before they block the front-only quote.
      if (
        !hasBackImage &&
        (hasBackDesignDirection ||
          ((phase === "back_design" || phase === "back_review") &&
            (hasText(formState.backPrompt) ||
              hasBackUploadReference(formState))))
      ) {
        return blockedResult(
          "back_image_required_for_quote",
          ["backImage"],
          "Create a preview of the back before including it in your quote, or ask me to remove the back details and quote the front alone.",
          ["generate_dress_back"]
        );
      }
      return ALLOWED_RESULT;
    }

    case "modify_dress_front":
      if (!hasFrontImage) {
        return blockedResult(
          "front_image_required",
          ["frontImage"],
          "Generate the front view before modifying it.",
          ["generate_dress_front"]
        );
      }
      return ALLOWED_RESULT;

    case "start_virtual_try_on":
      if (!hasFrontImage) {
        return blockedResult(
          "front_image_required",
          ["frontImage"],
          "Generate the front view before trying the dress on.",
          ["generate_dress_front"]
        );
      }
      return ALLOWED_RESULT;

    case "modify_dress_back":
      if (!hasBackImage) {
        return blockedResult(
          "back_image_required",
          ["backImage"],
          "Generate the back view before modifying it.",
          ["generate_dress_back"]
        );
      }
      return ALLOWED_RESULT;

    default:
      return ALLOWED_RESULT;
  }
}
